import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload,
  AttachFile,
  PictureAsPdf,
  Delete,
  InsertDriveFile,
  GetApp,
  CheckCircle,
  Visibility,
  Close,
} from "@mui/icons-material";

interface PresignedUploadData {
  uploadUrl: string;
  fields: Record<string, string>;
  fileUrl: string;
  filename: string;
}

interface FileAttachmentProps {
  onFileChange?: (file: File | null) => void;
  onGetUploadUrl?: (
    filename: string,
    fileSize: number,
  ) => Promise<PresignedUploadData>;
  onConfirmUpload?: (
    fileUrl: string,
    originalFilename: string,
  ) => Promise<void>;
  // Called when the direct S3 upload fails (between presign and confirm), so
  // parents can reset their uploading state and surface the error. Without
  // this, a failed S3 POST leaves parent-driven spinners stuck forever.
  onUploadError?: (message: string) => void;
  onDelete?: () => Promise<void>;
  disabled?: boolean;
  acceptedFileTypes?: string;
  maxFileSize?: number; // in MB
  uploading?: boolean;
  uploadError?: string | null;
  existingFileUrl?: string | null;
  existingFileName?: string | null;
  autoUpload?: boolean; // Whether to upload immediately when file is selected
  // FL-318: keep the drop-zone visible alongside an existing file so a new
  // upload can replace it (used when a submitted student edits their work).
  allowReplace?: boolean;
  replaceHelperText?: string;
}

export const FileAttachment: React.FC<FileAttachmentProps> = ({
  onFileChange,
  onGetUploadUrl,
  onConfirmUpload,
  onUploadError,
  onDelete,
  disabled = false,
  acceptedFileTypes = ".pdf,.doc,.docx,.txt",
  maxFileSize = 10,
  uploading = false,
  uploadError = null,
  existingFileUrl = null,
  existingFileName = null,
  autoUpload = false,
  allowReplace = false,
  replaceHelperText,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Effect to handle initial existing file
  useEffect(() => {
    if (existingFileUrl) {
      setIsUploaded(true);
    }
  }, [existingFileUrl]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file size
    if (file.size > maxFileSize * 1024 * 1024) {
      setError(`File size must be less than ${maxFileSize}MB`);
      return;
    }

    // Validate file type
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(fileExtension)) {
      setError(`Only these file types are allowed: ${acceptedFileTypes}`);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setIsUploaded(false);

    // Create URL for file preview
    const url = URL.createObjectURL(file);
    setFileUrl(url);

    onFileChange?.(file);

    // Auto-upload if enabled and upload functions provided
    if (autoUpload && onGetUploadUrl && onConfirmUpload) {
      try {
        await handleDirectUpload(file);
      } catch (error) {
        console.error("File upload failed:", error);
        // Error handling is done via uploadError prop
      }
    }
  };

  const handleDirectUpload = async (file: File) => {
    if (!onGetUploadUrl || !onConfirmUpload) return;

    try {
      // Step 1: Get pre-signed URL
      const uploadData = await onGetUploadUrl(file.name, file.size);

      // Step 2: Upload directly to S3
      const formData = new FormData();
      Object.keys(uploadData.fields).forEach((key) => {
        formData.append(key, uploadData.fields[key]);
      });
      formData.append("file", file);

      let response: Response;
      try {
        response = await fetch(uploadData.uploadUrl, {
          method: "POST",
          body: formData,
        });
      } catch {
        // fetch rejects with an opaque TypeError ("Failed to fetch") when the
        // request never gets a response — network drop, or the storage bucket
        // rejecting the browser's CORS preflight (FL-289). Surface something
        // actionable instead of the raw browser error.
        throw new Error(
          "Upload failed: the file storage service could not be reached. " +
            "Please check your connection and try again. If the problem " +
            "persists, contact support.",
        );
      }

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      // Step 3: Confirm upload with backend
      await onConfirmUpload(uploadData.fileUrl, file.name);
      setIsUploaded(true);
    } catch (error) {
      console.error("Direct upload failed:", error);
      const message =
        error instanceof Error ? error.message : "File upload failed";
      if (onUploadError) {
        // Let the parent reset its uploading state and show the error
        onUploadError(message);
      } else {
        // No parent handler: surface the failure locally so the user is not
        // left staring at a spinner with no feedback
        setError(message);
      }
      throw error;
    }
  };

  const handleManualUpload = async () => {
    if (selectedFile && onGetUploadUrl && onConfirmUpload) {
      try {
        await handleDirectUpload(selectedFile);
      } catch (error) {
        console.error("File upload failed:", error);
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    handleFiles(e.target.files);
  };

  const handleBrowseClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleRemoveFile = async () => {
    // FL-284: once the upload is confirmed server-side, removing the file
    // must delete it there too — clearing only local state left the server
    // copy in place (the "existing file" card immediately reappeared).
    if (isUploaded && onDelete) {
      try {
        await onDelete();
      } catch {
        // Parent surfaces the error via uploadError; keep the card.
        return;
      }
      setIsUploaded(false);
    }

    // Clean up the object URL
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
    }

    setSelectedFile(null);
    setFileUrl(null);
    setError(null);
    setPreviewOpen(false);
    onFileChange?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (extension === "pdf") {
      return <PictureAsPdf sx={{ color: "error.main", mr: 2, fontSize: 32 }} />;
    }
    return (
      <InsertDriveFile sx={{ color: "primary.main", mr: 2, fontSize: 32 }} />
    );
  };

  // Which selected-file types can render a meaningful in-app preview
  const canPreviewFile = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    return ["pdf", "png", "jpg", "jpeg"].includes(extension ?? "");
  };

  const renderFilePreview = () => {
    if (!selectedFile || !fileUrl) return null;

    const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();

    if (fileExtension === "pdf") {
      return (
        <Box
          sx={{
            height: "70vh",
            width: "100%",
            border: "1px solid #ddd",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Preview"
          />
        </Box>
      );
    } else if (
      fileExtension === "png" ||
      fileExtension === "jpg" ||
      fileExtension === "jpeg"
    ) {
      return (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            bgcolor: "action.hover",
            borderRadius: 1,
            p: 1,
          }}
        >
          <img
            src={fileUrl}
            alt={selectedFile.name}
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
            }}
          />
        </Box>
      );
    } else if (fileExtension === "txt") {
      return (
        <Box
          sx={{
            height: 200,
            width: "100%",
            border: "1px solid #ddd",
            borderRadius: 1,
            p: 2,
            bgcolor: "#f9f9f9",
            overflow: "auto",
          }}
        >
          <Typography
            variant="body2"
            component="pre"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {/* For text files, we could read the content, but for now show a placeholder */}
            Text file preview available after upload
          </Typography>
        </Box>
      );
    } else {
      // For other file types (doc, docx, etc.), show a placeholder
      return (
        <Box
          sx={{
            height: 200,
            bgcolor: "#f5f5f5",
            border: "1px solid #ddd",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          {getFileIcon(selectedFile.name)}
          <Typography variant="body2" color="text.secondary">
            {selectedFile.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Preview available after submission
          </Typography>
        </Box>
      );
    }
  };

  const dropZone = (
    <Paper
      elevation={1}
      sx={{
        p: 4,
        border: dragActive ? "2px dashed #1976d2" : "2px dashed #ccc",
        borderRadius: 2,
        textAlign: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        bgcolor: dragActive ? "action.selected" : "background.paper",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s ease-in-out",
        "&:hover": disabled
          ? {}
          : {
              bgcolor: "action.hover",
              borderColor: "primary.main",
            },
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={handleBrowseClick}
    >
      <CloudUpload sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>
        Drag & drop your file here
      </Typography>
      {/* Only meaningful while an existing file would actually be replaced */}
      {replaceHelperText && existingFileUrl && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
          {replaceHelperText}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        or click to browse files
      </Typography>
      <Button
        variant="outlined"
        startIcon={<AttachFile />}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          handleBrowseClick();
        }}
      >
        Choose File
      </Button>
      <Typography variant="caption" display="block" sx={{ mt: 2 }}>
        Supported formats: {acceptedFileTypes} (Max {maxFileSize}MB)
      </Typography>
    </Paper>
  );

  return (
    <Box>
      {/* <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
        File Attachment (Optional)
      </Typography> */}

      {/* Show existing file if present */}
      {existingFileUrl && !selectedFile && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircle
                sx={{ color: "success.main", mr: 2, fontSize: 32 }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {"Uploaded File"}
                </Typography>
                <Typography variant="body2" color="success.main">
                  File uploaded successfully
                </Typography>
              </Box>
              <Link
                href={existingFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center", mr: 2 }}
              >
                <GetApp sx={{ mr: 1 }} />
              </Link>
              {!disabled && onDelete && (
                <IconButton
                  onClick={() => {
                    onDelete()
                      .then(() => {
                        setSelectedFile(null);
                        setFileUrl(null);
                        setIsUploaded(false);
                        onFileChange?.(null);
                      })
                      .catch(() => {
                        /* error is shown by parent via uploadError */
                      });
                  }}
                  color="error"
                  size="small"
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* FL-318 replace mode: keep the drop-zone available below the
          existing-file card so a new upload can replace the old file */}
      {existingFileUrl && !selectedFile && allowReplace && !disabled && (
        <Box sx={{ mt: 2 }}>{dropZone}</Box>
      )}

      {!selectedFile && !existingFileUrl ? (
        dropZone
      ) : selectedFile ? (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {uploading ? (
                <CircularProgress size={32} sx={{ mr: 2 }} />
              ) : isUploaded ? (
                <CheckCircle
                  sx={{ color: "success.main", mr: 2, fontSize: 32 }}
                />
              ) : (
                getFileIcon(selectedFile.name)
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                  {uploading && " - Uploading..."}
                  {isUploaded && " - Uploaded successfully"}
                </Typography>
              </Box>
              {!uploading && fileUrl && canPreviewFile(selectedFile.name) && (
                <Tooltip title="Preview">
                  <IconButton
                    onClick={() => setPreviewOpen(true)}
                    size="small"
                    aria-label="Preview file"
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>
              )}
              {!uploading && (
                <IconButton
                  onClick={handleRemoveFile}
                  disabled={disabled}
                  color="error"
                  size="small"
                >
                  <Delete />
                </IconButton>
              )}
            </Box>

            {/* Upload button for manual upload */}
            {!autoUpload &&
              !isUploaded &&
              !uploading &&
              onGetUploadUrl &&
              onConfirmUpload && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleManualUpload}
                    disabled={disabled}
                    startIcon={<CloudUpload />}
                  >
                    Upload File
                  </Button>
                </Box>
              )}
          </CardContent>
        </Card>
      ) : null}

      {/* File preview modal (triggered by the preview icon button) */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Box
            component="span"
            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {selectedFile?.name}
          </Box>
          <IconButton
            onClick={() => setPreviewOpen(false)}
            size="small"
            aria-label="Close preview"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderFilePreview()}</DialogContent>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {uploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {uploadError}
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        onChange={handleFileInput}
        style={{ display: "none" }}
        disabled={disabled}
      />
    </Box>
  );
};
